import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import PasswordInput from '@/components/PasswordInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Self-serve account deletion modal.
 *
 * Two-step confirmation:
 *   1. Type the word "DELETE" (must match exactly).
 *   2. Re-enter current password (skipped for Google-OAuth users — the
 *      backend accepts that case since the auth cookie proves ownership).
 *
 * On success we clear the local auth state via `onDeleted` and let the
 * parent navigate away — backend has already invalidated the cookie.
 */
const DeleteAccountDialog = ({ open, onOpenChange, hasPassword = true, onDeleted }) => {
  const [confirmation, setConfirmation] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setConfirmation('');
    setPassword('');
    setBusy(false);
  };

  const canSubmit =
    !busy && confirmation === 'DELETE' && (!hasPassword || password.length > 0);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/user/me`, {
        withCredentials: true,
        data: {
          confirmation,
          current_password: hasPassword ? password : undefined,
        },
      });
      toast.success('Account deleted');
      onOpenChange(false);
      reset();
      onDeleted?.();
    } catch (err) {
      console.error('Delete account error:', err);
      toast.error(err.response?.data?.detail || 'Could not delete account');
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DialogContent className="sm:max-w-md" data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Delete your account
          </DialogTitle>
          <DialogDescription>
            This will <strong>permanently</strong> remove your profile, saved routes, reviews,
            badge history, push tokens, and sessions. Your partner-entity admin assignments will
            be unlinked. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="delete-confirm-input">
              Type <span className="font-mono font-bold">DELETE</span> to confirm
            </Label>
            <Input
              id="delete-confirm-input"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder="DELETE"
              autoComplete="off"
              data-testid="delete-confirm-input"
            />
          </div>

          {hasPassword && (
            <div className="space-y-2">
              <Label htmlFor="delete-pw-input">Current password</Label>
              <PasswordInput
                id="delete-pw-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                data-testid="delete-pw-input"
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="delete-account-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="delete-account-confirm"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Delete my account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteAccountDialog;
